<template>
  <v-container fluid class="repCostCenterContainer">
    <v-layout row wrap>
      <v-flex xs12>
        <data-card color="primary" :title="$tc('report.balancesOfCostCenters.title', 2)">
          <template slot="title-items">
            <v-menu offset-y bottom left nudge-bottom="5" transition="slide-y-transition">
              <template v-slot:activator="{ on }">
                <v-btn icon small color="secondary" v-on="on">
                  <v-icon color="white">more_vert</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-tile v-if="isUserAdministrator" :href="exportAcrossFacilitiesUrl" target="_blank" >
                  <v-list-tile-title>{{ $t('report.balancesOfCostCenters.generalFacilitiesExport') }}</v-list-tile-title>
                </v-list-tile>
                <v-list-tile v-if="!withGeneralLedgerAccounts" @click="onWithGeneralLedgerAccounts" >
                  <v-list-tile-title>{{ $t('report.balancesOfCostCenters.showGeneralLedgerAccounts') }}</v-list-tile-title>
                </v-list-tile>
                <v-list-tile v-if="withGeneralLedgerAccounts" @click="onWithoutGeneralLedgerAccounts" >
                  <v-list-tile-title>{{ $t('report.balancesOfCostCenters.hideGeneralLedgerAccounts') }}</v-list-tile-title>
                </v-list-tile>
                <v-list-tile :to="singleEntriesUrl" >
                  <v-list-tile-title>{{ $tc('common.singleEntry', 2) }}</v-list-tile-title>
                </v-list-tile>
                <v-list-tile :href="exportUrl" target="_blank" >
                  <v-list-tile-title>{{ $t('report.balancesOfCostCenters.export') }}</v-list-tile-title>
                </v-list-tile>
              </v-list>
            </v-menu>
          </template>
          <template slot="controls">
                <v-flex xs12 lg7>
                  <v-layout row>
                    <v-flex xs4>
                      <v-autocomplete
                        hide-details
                        prepend-inner-icon="filter_list"
                        v-model="selectedYear"
                        :items="years"
                        :label="$tc('common.year', 1)"
                        :placeholder="$tc('common.year', 1)"
                        dark
                        solo-inverted
                        flat
                        class="small">
                      </v-autocomplete>
                    </v-flex>
                    <v-flex xs4>
                      <v-autocomplete
                        hide-details
                        prepend-inner-icon="filter_list"
                        v-model="selectedPeriodAt"
                        :items="bookingPeriods"
                        :label="$t('report.balancesOfCostCenters.periodAt')"
                        :placeholder="$t('report.balancesOfCostCenters.periodAt')"
                        dark
                        solo-inverted
                        flat
                        class="small">
                      </v-autocomplete>
                    </v-flex>
                    <v-flex xs4>
                      <v-autocomplete
                        hide-details
                        prepend-inner-icon="filter_list"
                        v-model="selectedPeriodTo"
                        :items="bookingPeriods"
                        :label="$t('report.balancesOfCostCenters.periodTo')"
                        :placeholder="$t('report.balancesOfCostCenters.periodTo')"
                        dark
                        solo-inverted
                        flat
                        class="small">
                      </v-autocomplete>
                    </v-flex>
                  </v-layout>
                </v-flex>
                <v-flex xs12 lg5>
                  <v-layout row>
                    <v-flex xs8>
                      <v-autocomplete
                        hide-details
                        prepend-inner-icon="filter_list"
                        v-model="selectedFacilityId"
                        :items="facilityList"
                        item-text="displayName"
                        item-value="id"
                        :label="$tc('common.facility', 1)"
                        :placeholder="$tc('common.facility', 1)"
                        :loading="isLoadingFacilities"
                        dark
                        solo-inverted
                        flat
                        class="small">
                        </v-autocomplete>
                    </v-flex>
                    <v-flex xs4>
                      <data-card-search-field v-model="search"></data-card-search-field>
                    </v-flex>
                </v-layout>
                </v-flex>
          </template>
          <hasy-data-table
            ref="dataTable"
            :headers="headers"
            :items="visibleItems"
            :loading="isLoading"
            :selected-item.sync="selectedItem"
            :rows-per-page-items="[-1]"
            :pagination.sync="pagination"
            :search="search"
            :row-classes="tableRowClasses"
            :custom-sort="sortItems"
            >
          </hasy-data-table>
        </data-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { Api } from '@/api/apiAccess'
import { mapGetters } from 'vuex'
import Routes from '@/router/routes'
import mutationtypes from '@/store/modules/report/balancesofcostcenters/mutation-types'
import moduleNames from '@/store/moduleNames'
import { storedSelectedItemMixin, navigationBackButtonStateMixin, storedPaginationMixin, storedTableSearchMixin, storedPropertyMixin, selectYearAdvancedMixin, pageTitleMixin } from '@/mixins/mixins'

const storeModule = moduleNames.REPORTBALANCESOFCOSTCENTERS

export default {
  props: {
    yearParam: {
      default: undefined
    },
    periodFromParam: {
      default: undefined
    },
    periodToParam: {
      default: undefined
    },
    facilityParam: {
      default: undefined
    }
  },
  mixins: [
    storedSelectedItemMixin(storeModule, mutationtypes.UPDATE_COSTCENTERLISTSELECT_STATE, 'costCenterListSelectedState', 'selectedItem'),
    navigationBackButtonStateMixin(storeModule, mutationtypes.UPDATE_COSTCENTERNAVIGATIONBACKBUTTON_STATE, 'costCenterNavigationBackButtonState'),
    storedPaginationMixin(storeModule + '/pagination'),
    storedTableSearchMixin(storeModule, mutationtypes.UPDATE_COSTCENTERLISTTABLESEARCH_STATE, 'costCenterListTableSearchState', 'dataTableSearch'),
    storedPropertyMixin('selectedYear', storeModule, mutationtypes.UPDATE_COSTCENTERLIST_SELECTED_YEAR, 'selectedYear', 'year'),
    storedPropertyMixin('selectedPeriodAt', storeModule, mutationtypes.UPDATE_COSTCENTERLIST_SELECTED_PERIODAT, 'selectedPeriodAt', 'period'),
    storedPropertyMixin('selectedPeriodTo', storeModule, mutationtypes.UPDATE_COSTCENTERLIST_SELECTED_PERIODTO, 'selectedPeriodTo', 'period'),
    storedPropertyMixin('selectedFacilityId', storeModule, mutationtypes.UPDATE_COSTCENTERLIST_SELECTED_FACILITY, 'selectedFacility', 'facility'),
    storedPropertyMixin('withGeneralLedgerAccounts', storeModule, mutationtypes.UPDATE_COSTCENTERLIST_WITH_GENERAL_LEDGER_ACCOUNTS, 'withGeneralLedgerAccounts', 'withGeneralLedgerAccounts'),
    selectYearAdvancedMixin,
    pageTitleMixin
  ],
  data () {
    return {
      navigationBackButtonState: { isVisible: false },
      isLoading: false,
      items: [],
      selected: {},
      loading: false,
      headers: [
        {
          text: this.$t('report.balancesOfCostCenters.group'),
          align: 'left',
          value: 'costCenterGroup',
          width: '10%',
          dataType: 'string',
          contentFormat: (v) => this.translateColumnData(v),
          contentVisible: (v, i) => i.costCenter === '{{sumCostCenter}}',
          contentClasses: 'no-wrap'
        },
        {
          text: this.$t('report.balancesOfCostCenters.costCenter'),
          align: 'left',
          value: 'costCenter',
          width: '17%',
          dataType: 'string',
          contentFormat: (v) => this.translateColumnData(v),
          contentVisible: (v, i) => i.generalLedgerAccount === '{{sumGeneralLedgerAccount}}',
          contentClasses: 'no-wrap',
          actions: [{
            icon: 'zoom_in',
            title: this.$t('report.balancesOfCostCenters.details'),
            color: 'primary',
            classes: ['inline-action'],
            to: (item) => { return { name: Routes.SINGLE_ENTRIES, query: { y: this.selectedYear, pf: this.selectedPeriodAt, pt: this.selectedPeriodTo, f: this.selectedFacilityId, cc: item.costCenter.substr(0, 7) } } },
            visible: (item) => item.costCenter !== '{{sumCostCenter}}' && item.generalLedgerAccount === '{{sumGeneralLedgerAccount}}'
          }]
        },
        {
          text: this.$t('report.balancesOfCostCenters.account'),
          align: 'left',
          value: 'generalLedgerAccount',
          width: '18%',
          dataType: 'string',
          contentFormat: (v) => this.translateColumnData(v),
          contentClasses: 'no-wrap',
          actions: [{
            icon: 'zoom_in',
            title: this.$t('report.balancesOfCostCenters.details'),
            color: 'primary',
            classes: ['inline-action'],
            to: (item) => { return { name: Routes.SINGLE_ENTRIES, query: { y: this.selectedYear, pf: this.selectedPeriodAt, pt: this.selectedPeriodTo, f: this.selectedFacilityId, cc: item.costCenter.substr(0, 7), gla: item.generalLedgerAccount.substr(0, 5) } } },
            visible: (item) => item.generalLedgerAccount !== '{{sumGeneralLedgerAccount}}'
          }]
        },
        {
          text: this.$t('report.balancesOfCostCenters.planRevenue'),
          align: 'right',
          value: 'planRevenue',
          width: '10%',
          dataType: 'currency',
          contentAlign: 'right',
          classes: 'wrap-header',
          contentFormat: (v) => this.$n(v, 'numberWith2DecimalGrouping')
        },
        {
          text: this.$t('report.balancesOfCostCenters.planIssue'),
          align: 'right',
          value: 'planIssue',
          width: '10%',
          dataType: 'currency',
          contentAlign: 'right',
          classes: 'wrap-header',
          contentFormat: (v) => this.$n(v, 'numberWith2DecimalGrouping')
        },
        {
          text: this.$t('report.balancesOfCostCenters.actualRevenue'),
          align: 'right',
          value: 'actualRevenue',
          width: '10%',
          dataType: 'currency',
          contentAlign: 'right',
          classes: 'wrap-header',
          contentFormat: (v) => this.$n(v, 'numberWith2DecimalGrouping')
        },
        {
          text: this.$t('report.balancesOfCostCenters.actualIssue'),
          align: 'right',
          value: 'actualIssue',
          width: '10%',
          dataType: 'currency',
          contentAlign: 'right',
          classes: 'wrap-header',
          contentFormat: (v) => this.$n(v, 'numberWith2DecimalGrouping')
        },
        {
          text: this.$t('report.balancesOfCostCenters.balance'),
          align: 'right',
          value: 'balance',
          width: '10%',
          dataType: 'currency',
          contentAlign: 'right',
          contentFormat: (v) => this.$n(v, 'numberWith2DecimalGrouping')
        }
      ],
      tableRowClasses: (item) => {
        if (item.costCenterGroup === '{{sumTotal}}') {
          return 'sumTotal'
        } else if (item.costCenter === '{{sumCostCenter}}') {
          return 'sumAllCostCenters'
        } else if (item.generalLedgerAccount === '{{sumGeneralLedgerAccount}}') {
          return 'sumAllGeneralLedgerAccounts'
        }
        return 'sumGeneralLedgerAccount'
      },
      bookingPeriods: [...Array.from(new Array(15), (x, i) => {
        if (i < 12) {
          let objDate = new Date()
          objDate.setDate(1)
          objDate.setMonth(i)
          return {
            text: objDate.toLocaleString(this.$i18n.locale, { month: 'long' }),
            value: i + 1
          }
        } else {
          return {
            text: this.$tc('report.balancesOfCostCenters.annualAccounts', i - 12),
            value: i + 1
          }
        }
      })],
      facilityList: [],
      isLoadingFacilities: false,
      isInitialized: false,
      isLoadingPastYear: false
    }
  },
  computed: {
    ...mapGetters(['isUserAdministrator']),
    visibleItems () {
      let allItems = this.items || []
      let wgla = this.withGeneralLedgerAccounts
      if (wgla) {
        return allItems
      } else {
        return allItems.filter(i => i.generalLedgerAccount === '{{sumGeneralLedgerAccount}}')
      }
    },
    exportUrl () {
      let paramCreator = Api.ReportParamCreator
      let year = this.selectedYear
      let periodAt = this.selectedPeriodAt
      let periodTo = this.selectedPeriodTo
      let facility = this.selectedFacilityId
      let culture = this.$i18n.locale
      let params = paramCreator.reportExportBalancesOfCostCenters(year, periodAt, periodTo, facility, culture)
      let relativeUrl = params.url
      let basePath = Api.BasePath

      if (relativeUrl == null || relativeUrl.length <= 0 || basePath == null || basePath.length <= 0) {
        return null
      }

      return basePath + relativeUrl
    },
    exportAcrossFacilitiesUrl () {
      let paramCreator = Api.ReportParamCreator
      let year = this.selectedYear
      let periodAt = this.selectedPeriodAt
      let periodTo = this.selectedPeriodTo
      let culture = this.$i18n.locale
      let params = paramCreator.reportExportBalancesOfCostCentersAcrossFacilities(year, periodAt, periodTo, null, culture)
      let relativeUrl = params.url
      let basePath = Api.BasePath

      if (relativeUrl == null || relativeUrl.length <= 0 || basePath == null || basePath.length <= 0) {
        return null
      }

      return basePath + relativeUrl
    },
    singleEntriesUrl () {
      return { name: Routes.SINGLE_ENTRIES, query: { y: this.selectedYear, pf: this.selectedPeriodAt, pt: this.selectedPeriodTo, f: this.selectedFacilityId } }
    }
  },
  watch: {
    selectedYear (v) {
      this.loadData(1)
    },
    selectedPeriodAt (v) {
      this.loadData(2)
    },
    selectedPeriodTo (v) {
      this.loadData(3)
    },
    selectedFacilityId (v) {
      this.loadData(4)
    }
  },
  methods: {
    sortItems (items, index, isDescending) {
      if (index == null || index.trim().length <= 0 || items == null || items.length <= 0) {
        return items
      }

      let sortData = []
      let header = this.headers.find(h => h.value === index)
      let dataType = (header || {}).dataType || ''
      let sortDir = isDescending ? 'desc' : 'asc'

      let sortItems = (itemsToSort) => {
        return this.$refs.dataTable.sortItems(itemsToSort, index, sortDir, dataType)
      }

      if (this.search != null && this.search.length > 0) {
        return sortItems(items)
      }

      let regex = /^\{{2}(.+)\}{2}$/
      let sumItems = items.filter(x => regex.test(x.costCenter))
      sumItems = sortItems(sumItems)

      sumItems.forEach(item => {
        sortData.push(item)
        let subSumItems = items.filter(x => x.costCenterGroup === item.costCenterGroup && x !== item && regex.test(x.generalLedgerAccount))
        subSumItems = sortItems(subSumItems)
        subSumItems.forEach(item2 => {
          sortData.push(item2)
          let glaItems = items.filter(x => x.costCenterGroup === item2.costCenterGroup && x.costCenter === item2.costCenter && x !== item && x !== item2)
          glaItems = sortItems(glaItems)
          sortData.push(...glaItems)
        })
      })

      return sortData
    },
    loadFacilities () {
      this.isLoadingFacilities = true
      let facilityApi = Api.Facility

      return facilityApi.facilityGetAllAuthorizedActive(4)
        .then(r => {
          this.facilityList = r.data
          return Promise.resolve()
        },
        err => {
          return Promise.reject(err)
        })
        .finally(() => {
          this.isLoadingFacilities = false
        })
    },
    loadData (callType) {
      if (!this.isInitialized) {
        return Promise.resolve()
      }

      if (this.selectedFacilityId == null) {
        this.items = []
        return Promise.resolve()
      }

      if (this.isLoadingPastYear) {
        return Promise.resolve()
      }

      let year = this.selectedYear

      if (callType === 1) {
        if (this.selectedYear < new Date().getFullYear()) {
          this.isLoadingPastYear = true
          this.selectedPeriodAt = this.bookingPeriods[0].value
          this.selectedPeriodTo = this.bookingPeriods[this.bookingPeriods.length - 1].value
        }
      }

      let periodAt = this.selectedPeriodAt
      let periodTo = this.selectedPeriodTo
      let facility = this.selectedFacilityId

      this.isLoading = true
      let ep = Api.Report
      return ep.reportGetBalancesOfCostCenters(year, periodAt, periodTo, facility)
        .then(r => {
          this.items = r.data
          return Promise.resolve()
        },
        err => {
          return Promise.reject(err)
        })
        .finally(() => {
          this.isLoading = false
          this.isLoadingPastYear = false
          this.restoreSelectedItem()
        })
    },
    onWithGeneralLedgerAccounts () {
      this.withGeneralLedgerAccounts = true
    },
    onWithoutGeneralLedgerAccounts () {
      this.withGeneralLedgerAccounts = false
    },
    translateColumnData (value) {
      if (value == null) {
        return value
      }
      let regex = /^\{{2}(.+)\}{2}$/
      let matches = regex.exec(value)
      if (matches !== null && matches.length === 2) {
        let str = matches[1]
        let translateString = this.$t('report.balancesOfCostCenters.' + str)
        return translateString
      } else {
        return value
      }
    },
    init () {
      if (this.selectedYear == null) {
        this.selectedYear = new Date().getFullYear()
      }
      if (this.selectedPeriodAt == null) {
        this.selectedPeriodAt = 1
      }
      if (this.selectedPeriodTo == null) {
        this.selectedPeriodTo = new Date().getMonth() + 1
      }

      if (this.yearParam != null) {
        this.selectedYear = this.yearParam
      }

      if (this.periodFromParam != null) {
        this.selectedPeriodAt = this.periodFromParam
      }

      if (this.periodToParam != null) {
        this.selectedPeriodTo = this.periodToParam
      }

      if (this.facilityParam != null) {
        this.selectedFacilityId = this.facilityParam
      }

      this.loadFacilities()
        .then(() => {
          if (this.selectedFacilityId == null) {
            let facilities = this.facilityList || []
            let firstFacility = facilities[0]
            if (firstFacility != null) {
              this.selectedFacilityId = firstFacility.id
            }
          }
          this.$nextTick(() => {
            this.isInitialized = true
            this.loadData(0)
          })
        })
    }
  },
  created () {
    this.init()
  }
}

</script>

<style lang="scss">
.repCostCenterContainer {

  .data-table {

    tbody {

      > tr {

        &:not(:last-child) {
          border-bottom: none;
        }

        &.sumTotal {
          border-bottom: 1px solid rgba(255,0,0,.4);
        }

        &.sumAllCostCenters {
          border-bottom: 1px dotted rgba(255,0,0,.4);
        }

        &.sumTotal,
        &.sumAllCostCenters {

          > td {
            font-weight: 500;
          }
        }

        &.sumAllGeneralLedgerAccounts {

          > td {
            font-style: italic;
          }
        }
      }
    }
  }
}
</style>
